import { err, Result as NTResult, ok } from 'neverthrow';

import type { Result } from '../result.js';
import { NaiveDate } from './naive_date.js';
import { Period, type PeriodOps } from './period.js';
import { YearMonth } from './year_month.js';

export type NaiveDateSpanDto = {
  first: string;
  last: string;
};

export const naiveDateOps: PeriodOps<NaiveDate> = {
  compare: NaiveDate.compare,
  next: NaiveDate.next,
  display: NaiveDate.display,
};

function displayYearMonth(date: NaiveDate): string {
  return date.slice(0, 7);
}

const dateDivider = '--';

export class NaiveDateSpan extends Period<NaiveDate> {
  public constructor(firstInPeriod: NaiveDate, lastInPeriod: NaiveDate) {
    super(firstInPeriod, lastInPeriod, naiveDateOps);
  }

  public displayShort(): string {
    return `${displayYearMonth(this.firstInPeriod)} – ${displayYearMonth(this.lastInPeriod)}`;
  }

  public displayEndDate(): string {
    return this.lastInPeriod;
  }

  public displayFull(): string {
    return `${this.firstInPeriod} – ${this.lastInPeriod}`;
  }

  public numberOfDaysInPeriod(): number {
    return NaiveDate.dateDiffInDays(this.firstInPeriod, this.lastInPeriod);
  }

  public static fromDateSpanDto(dateSpan: NaiveDateSpanDto): NTResult<NaiveDateSpan, string> {
    try {
      const first = NaiveDate.fromIsoString(dateSpan.first);
      const last = NaiveDate.fromIsoString(dateSpan.last);
      if (first > last) {
        return err(
          `Invalid Time.NaiveDateSpanDto: first date (${NaiveDate.toIsoStringBasic(
            first,
          )}) must be earlier than last date (${NaiveDate.toIsoStringBasic(last)}).`,
        );
      }
      return ok(new NaiveDateSpan(first, last));
    } catch (e) {
      return err(e as string);
    }
  }

  public static fromString(s: string): Result<NaiveDateSpan> {
    const parts = s.split(dateDivider);
    try {
      return new NaiveDateSpan(
        NaiveDate.fromIsoString(parts[0]),
        NaiveDate.fromIsoString(parts[1]),
      );
    } catch (e) {
      return new Error(e as string);
    }
  }

  public static fromPeriod(period: Period<NaiveDate>): NaiveDateSpan {
    return new NaiveDateSpan(period.first(), period.last());
  }

  public static fromYear(year: string): NaiveDateSpan {
    const first = NaiveDate.fromIsoString(year + '-01-01');
    const last = NaiveDate.fromIsoString(year + '-12-31');

    return new NaiveDateSpan(first, last);
  }

  public override toString() {
    return `${this.firstInPeriod}${dateDivider}${this.lastInPeriod}`;
  }

  public toWASM() {
    return `${this.firstInPeriod} - ${this.lastInPeriod}`;
  }

  public static fromYearMonth(yearMonth: YearMonth): NaiveDateSpan {
    const first = NaiveDate.fromIsoString(yearMonth + '-01');
    const firstNextMonth = NaiveDate.fromIsoString(YearMonth.next(yearMonth) + '-01');
    const last = NaiveDate.previous(firstNextMonth);
    return new NaiveDateSpan(first, last);
  }
}
